/**
 * Whiteboard Page Component
 * 
 * A collaborative whiteboard using react-sketch-canvas that can be shared between
 * experts and users via a unique room link.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import styled from 'styled-components';
import { ReactSketchCanvas } from 'react-sketch-canvas';
import useAuthenticatedAxios from '../../auth/AuthenticatedAxios';
import { socket, onPrivateMessageReceived, sendPrivateMessage } from '../../Socket/socket';

// API base URL - fallback to localhost for development
// Remove trailing slash if present to avoid double slashes in URLs
const API_BASE = (process.env.REACT_APP_SERVER || 'http://localhost:3030').replace(/\/$/, '');

// ============================================
// STYLED COMPONENTS
// ============================================

const PageContainer = styled.div`
  width: 100%;
  height: 100vh;
  display: flex;
  flex-direction: column;
  background: #1a1a2e;
  overflow: hidden;
  box-sizing: border-box;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 20px;
  background: linear-gradient(135deg, #16213e 0%, #1a1a2e 100%);
  border-bottom: 1px solid rgba(99, 102, 241, 0.2);
  z-index: 100;
  min-height: 60px;
  flex-shrink: 0;
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const Title = styled.h1`
  font-size: 18px;
  font-weight: 600;
  color: #f8fafc;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const RoomId = styled.span`
  font-size: 12px;
  color: rgba(248, 250, 252, 0.5);
  font-weight: 400;
  background: rgba(99, 102, 241, 0.1);
  padding: 4px 10px;
  border-radius: 12px;
  border: 1px solid rgba(99, 102, 241, 0.2);
`;

const ParticipantsBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: rgba(248, 250, 252, 0.7);
  background: rgba(16, 185, 129, 0.1);
  padding: 6px 12px;
  border-radius: 20px;
  border: 1px solid rgba(16, 185, 129, 0.2);
`;

const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
  padding-right: 10px;
`;

const Button = styled.button`
  padding: 8px 16px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 6px;
  
  ${props => props.$primary ? `
    background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
    border: none;
    color: #fff;
    
    &:hover {
      box-shadow: 0 4px 15px rgba(99, 102, 241, 0.4);
    }
  ` : props.$danger ? `
    background: rgba(239, 68, 68, 0.1);
    border: 1px solid rgba(239, 68, 68, 0.3);
    color: #ef4444;
    
    &:hover {
      background: rgba(239, 68, 68, 0.2);
    }
  ` : `
    background: rgba(99, 102, 241, 0.1);
    border: 1px solid rgba(99, 102, 241, 0.3);
    color: #a5b4fc;
    
    &:hover {
      background: rgba(99, 102, 241, 0.2);
    }
  `}
`;

const CanvasContainer = styled.div`
  flex: 1;
  width: 100%;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f5f5f5;
  position: relative;
`;

const ShapeOverlay = styled.svg`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: ${props => props.$active ? 'auto' : 'none'};
  z-index: ${props => props.$active ? 10 : 5};
`;

const ToolSeparator = styled.div`
  width: 1px;
  height: 24px;
  background: rgba(99, 102, 241, 0.3);
  margin: 0 4px;
`;

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 20px;
  background: rgba(22, 33, 62, 0.95);
  border-bottom: 1px solid rgba(99, 102, 241, 0.2);
`;

const ToolbarGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ToolbarLabel = styled.span`
  font-size: 12px;
  color: rgba(248, 250, 252, 0.6);
  margin-right: 4px;
`;

const ColorPicker = styled.input`
  width: 32px;
  height: 32px;
  border: 2px solid rgba(99, 102, 241, 0.3);
  border-radius: 6px;
  cursor: pointer;
  background: none;
  padding: 0;
  
  &::-webkit-color-swatch-wrapper {
    padding: 2px;
  }
  
  &::-webkit-color-swatch {
    border: none;
    border-radius: 4px;
  }
`;

const SizeSlider = styled.input`
  width: 100px;
  height: 6px;
  border-radius: 3px;
  background: rgba(99, 102, 241, 0.2);
  outline: none;
  cursor: pointer;
  
  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    width: 16px;
    height: 16px;
    background: #6366f1;
    border-radius: 50%;
    cursor: pointer;
  }
`;

const SizeValue = styled.span`
  font-size: 12px;
  color: #a5b4fc;
  min-width: 30px;
`;

const ToolButton = styled.button`
  padding: 8px 12px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  background: ${props => props.$active ? 'rgba(99, 102, 241, 0.3)' : 'rgba(99, 102, 241, 0.1)'};
  border: 1px solid ${props => props.$active ? 'rgba(99, 102, 241, 0.5)' : 'rgba(99, 102, 241, 0.2)'};
  color: #a5b4fc;
  
  &:hover {
    background: rgba(99, 102, 241, 0.2);
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100vh;
  background: #1a1a2e;
  color: #f8fafc;
`;

const Spinner = styled.div`
  width: 40px;
  height: 40px;
  border: 3px solid rgba(99, 102, 241, 0.2);
  border-top-color: #6366f1;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 16px;
  
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100vh;
  background: #1a1a2e;
  color: #f8fafc;
  text-align: center;
  padding: 20px;
`;

const ErrorIcon = styled.div`
  font-size: 48px;
  margin-bottom: 16px;
`;

const StatusIndicator = styled.span`
  font-size: 12px;
  color: ${props => props.$saving ? '#fbbf24' : 'rgba(248, 250, 252, 0.5)'};
  display: flex;
  align-items: center;
  gap: 6px;
`;

// ============================================
// MAIN COMPONENT
// ============================================

const WhiteboardPage = () => {
  const { roomId } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated, isLoading: authLoading } = useAuth0();
  const authenticatedAxios = useAuthenticatedAxios();
  
  const canvasRef = useRef(null);
  const shapeOverlayRef = useRef(null);
  const [whiteboard, setWhiteboard] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [strokeColor, setStrokeColor] = useState('#000000');
  const [strokeWidth, setStrokeWidth] = useState(4);
  const [isEraser, setIsEraser] = useState(false);
  const saveTimeoutRef = useRef(null);
  
  // Shape drawing state
  const [activeTool, setActiveTool] = useState('pen'); // 'pen', 'line', 'rectangle', 'circle', 'triangle'
  const [shapes, setShapes] = useState([]); // Completed shapes
  const [currentShape, setCurrentShape] = useState(null); // Shape being drawn
  const [isDrawingShape, setIsDrawingShape] = useState(false);
  const [shapeStartPoint, setShapeStartPoint] = useState(null);

  // Load whiteboard data
  useEffect(() => {
    const loadWhiteboard = async () => {
      try {
        setIsLoading(true);
        
        // Debug logging
        const apiUrl = `${API_BASE}/api/whiteboard/${roomId}`;
        console.log('[Whiteboard] Loading from URL:', apiUrl);
        console.log('[Whiteboard] API_BASE:', API_BASE);
        console.log('[Whiteboard] roomId:', roomId);
        
        // Fetch whiteboard data (public endpoint)
        const response = await fetch(apiUrl);
        console.log('[Whiteboard] Response status:', response.status);
        
        if (!response.ok) {
          const errorText = await response.text();
          console.log('[Whiteboard] Error response:', errorText);
          if (response.status === 404) {
            setError('Whiteboard not found. It may have been closed or deleted.');
          } else {
            setError('Failed to load whiteboard.');
          }
          return;
        }
        
        const data = await response.json();
        console.log('[Whiteboard] Loaded whiteboard data:', data.whiteboard?.roomId);
        console.log('[Whiteboard] Scene data paths count:', data.whiteboard?.sceneData?.paths?.length || 0);
        setWhiteboard(data.whiteboard);
        
        // Load saved paths if available - use setTimeout to ensure canvas is mounted
        // Set isReceivingUpdate to prevent the load from triggering a save
        // Load shapes if they exist
        if (data.whiteboard?.sceneData?.shapes?.length > 0) {
          setShapes(data.whiteboard.sceneData.shapes);
          console.log('[Whiteboard] Loaded', data.whiteboard.sceneData.shapes.length, 'shapes');
        }
        
        if (data.whiteboard?.sceneData?.paths?.length > 0) {
          isReceivingUpdate.current = true;
          setTimeout(() => {
            if (canvasRef.current) {
              console.log('[Whiteboard] Loading saved paths to canvas');
              canvasRef.current.loadPaths(data.whiteboard.sceneData.paths);
            } else {
              console.warn('[Whiteboard] Canvas ref not ready for loading paths');
            }
            // Keep the flag true for a bit longer to let canvas settle
            setTimeout(() => {
              isReceivingUpdate.current = false;
              console.log('[Whiteboard] Ready for new drawings');
            }, 500);
          }, 100);
        }
        
        // Join the whiteboard
        const userName = user?.name || user?.email || 'Guest';
        const odId = user?.sub || `guest-${Date.now()}`;
        
        const joinResponse = await fetch(`${API_BASE}/api/whiteboard/${roomId}/join`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ odId, name: userName })
        });
        
        // Update whiteboard state with the response (includes updated participant list)
        if (joinResponse.ok) {
          const joinData = await joinResponse.json();
          if (joinData.whiteboard) {
            setWhiteboard(joinData.whiteboard);
            console.log('[Whiteboard] Updated participants:', joinData.whiteboard.participants?.length);
          }
        }
        
      } catch (err) {
        console.error('Error loading whiteboard:', err);
        setError('Failed to connect to whiteboard. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };

    if (roomId && !authLoading) {
      loadWhiteboard();
    }
  }, [roomId, user, authLoading]);

  // Track if we're receiving an update to avoid echo
  const isReceivingUpdate = useRef(false);
  
  // Track if initial sync was done
  const initialSyncDone = useRef(false);

  // Sync function to fetch latest changes from server
  const syncWhiteboard = useCallback(async () => {
    if (!roomId || !canvasRef.current) return;
    
    try {
      console.log('[Whiteboard] Syncing with server...');
      const response = await fetch(`${API_BASE}/api/whiteboard/${roomId}`);
      if (response.ok) {
        const data = await response.json();
        setWhiteboard(data.whiteboard);
        
        // Load shapes from server
        if (data.whiteboard?.sceneData?.shapes) {
          setShapes(data.whiteboard.sceneData.shapes);
        }
        
        // Load the latest paths from server
        if (data.whiteboard?.sceneData?.paths) {
          isReceivingUpdate.current = true;
          await canvasRef.current.clearCanvas();
          await canvasRef.current.loadPaths(data.whiteboard.sceneData.paths);
          setTimeout(() => { isReceivingUpdate.current = false; }, 100);
        }
        console.log('[Whiteboard] Synced with server');
      }
    } catch (err) {
      console.error('Error syncing whiteboard:', err);
    }
  }, [roomId]);

  // One-time auto-sync when page loads
  useEffect(() => {
    if (!isLoading && whiteboard && canvasRef.current && !initialSyncDone.current) {
      initialSyncDone.current = true;
      console.log('[Whiteboard] Running initial sync...');
      syncWhiteboard();
    }
  }, [isLoading, whiteboard, syncWhiteboard]);

  // Listen for whiteboard updates via WebSocket
  useEffect(() => {
    if (!roomId) return;

    console.log('[Whiteboard] Setting up WebSocket listener for room:', roomId);
    
    const handleWhiteboardUpdate = async (data) => {
      // Only process whiteboard updates
      if (data.type !== 'whiteboard_update') return;
      
      try {
        // Parse content if it's a string
        const content = typeof data.content === 'string' ? JSON.parse(data.content) : data.content;
        
        // Only process if it's for our room and not from us
        if (content?.roomId === roomId && 
            data.senderId !== user?.sub &&
            canvasRef.current) {
          console.log('[Whiteboard] Received update via WebSocket from:', data.senderId);
          isReceivingUpdate.current = true;
          
          // Load shapes from WebSocket update
          if (content?.shapes) {
            setShapes(content.shapes);
          }
          
          await canvasRef.current.clearCanvas();
          if (content?.paths) {
            await canvasRef.current.loadPaths(content.paths);
          }
          
          setTimeout(() => { isReceivingUpdate.current = false; }, 100);
        }
      } catch (err) {
        console.error('Error applying whiteboard update:', err);
        isReceivingUpdate.current = false;
      }
    };

    const cleanup = onPrivateMessageReceived(handleWhiteboardUpdate);
    return cleanup;
  }, [roomId, user?.sub]);

  // Auto-save changes with debounce and broadcast via WebSocket
  const saveChanges = useCallback(async () => {
    if (!roomId || !canvasRef.current || isReceivingUpdate.current) return;
    
    // Debounce saves
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    
    saveTimeoutRef.current = setTimeout(async () => {
      try {
        setIsSaving(true);
        
        const paths = await canvasRef.current.exportPaths();
        
        // Don't save empty canvas if whiteboard already had data
        // This prevents accidental data loss during load/sync
        if ((!paths || paths.length === 0) && whiteboard?.sceneData?.paths?.length > 0) {
          console.log('[Whiteboard] Skipping save - empty paths but DB has data');
          setIsSaving(false);
          return;
        }
        
        const sceneData = {
          paths,
          shapes, // Include shapes in save data
          strokeColor,
          strokeWidth
        };
        
        // Save to database
        console.log('[Whiteboard] Saving to database, paths count:', paths?.length || 0);
        const saveResponse = await fetch(`${API_BASE}/api/whiteboard/${roomId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ sceneData })
        });
        
        if (!saveResponse.ok) {
          console.error('[Whiteboard] Save failed:', saveResponse.status, await saveResponse.text());
        } else {
          console.log('[Whiteboard] Saved to database successfully');
        }

        // Broadcast update via WebSocket to other participants
        if (whiteboard?.participants) {
          whiteboard.participants.forEach(participant => {
            if (participant.odId !== user?.sub) {
              sendPrivateMessage({
                threadId: roomId,
                type: 'whiteboard_update',
                content: JSON.stringify({ roomId, paths, shapes }),
                receiverId: participant.odId,
                senderId: user?.sub,
                time: new Date().toISOString()
              });
            }
          });
          console.log('[Whiteboard] Broadcasted update to participants');
        }
        
      } catch (err) {
        console.error('Error saving whiteboard:', err);
      } finally {
        setIsSaving(false);
      }
    }, 500); // Save 0.5 seconds after last change (faster for real-time feel)
  }, [roomId, strokeColor, strokeWidth, shapes, whiteboard?.participants, user?.sub]);

  // Handle canvas change
  const handleChange = useCallback(() => {
    if (!isReceivingUpdate.current) {
      saveChanges();
    }
  }, [saveChanges]);

  // Copy share link
  const handleCopyLink = () => {
    const shareUrl = window.location.href;
    navigator.clipboard.writeText(shareUrl);
    // Could add a toast notification here
  };

  // Close whiteboard (expert only)
  const handleClose = async () => {
    if (!isAuthenticated) return;
    
    try {
      await authenticatedAxios.post(`/api/whiteboard/${roomId}/close`);
      navigate('/workspace');
    } catch (err) {
      console.error('Error closing whiteboard:', err);
    }
  };

  // Export as image
  const handleExport = async () => {
    if (!canvasRef.current) return;
    
    try {
      const dataUrl = await canvasRef.current.exportImage('png');
      const link = document.createElement('a');
      link.download = `whiteboard-${roomId}.png`;
      link.href = dataUrl;
      link.click();
    } catch (err) {
      console.error('Error exporting whiteboard:', err);
    }
  };

  // Clear canvas (Expert authority - clears everything and saves to DB)
  const handleClear = async () => {
    if (canvasRef.current) {
      canvasRef.current.clearCanvas();
      setShapes([]); // Clear shapes too
      
      // Force save empty state to database immediately
      try {
        const sceneData = {
          paths: [],
          shapes: [],
          strokeColor,
          strokeWidth
        };
        
        console.log('[Whiteboard] Clearing whiteboard - saving empty state to DB');
        const saveResponse = await fetch(`${API_BASE}/api/whiteboard/${roomId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ sceneData })
        });
        
        if (saveResponse.ok) {
          console.log('[Whiteboard] Cleared and saved to database');
          
          // Broadcast clear to other participants
          if (whiteboard?.participants) {
            whiteboard.participants.forEach(participant => {
              if (participant.odId !== user?.sub) {
                sendPrivateMessage({
                  threadId: roomId,
                  type: 'whiteboard_update',
                  content: JSON.stringify({ roomId, paths: [], shapes: [] }),
                  receiverId: participant.odId,
                  senderId: user?.sub,
                  time: new Date().toISOString()
                });
              }
            });
          }
        }
      } catch (err) {
        console.error('Error clearing whiteboard:', err);
      }
    }
  };

  // Undo
  const handleUndo = () => {
    if (canvasRef.current) {
      canvasRef.current.undo();
      saveChanges();
    }
  };

  // Redo
  const handleRedo = () => {
    if (canvasRef.current) {
      canvasRef.current.redo();
      saveChanges();
    }
  };

  // Toggle eraser
  const toggleEraser = () => {
    setIsEraser(!isEraser);
    if (canvasRef.current) {
      canvasRef.current.eraseMode(!isEraser);
    }
  };

  // Select tool
  const selectTool = (tool) => {
    setActiveTool(tool);
    setIsEraser(false);
    if (canvasRef.current) {
      canvasRef.current.eraseMode(false);
    }
  };

  // Get mouse position relative to overlay
  const getMousePosition = (e) => {
    const rect = shapeOverlayRef.current?.getBoundingClientRect();
    if (!rect) return { x: 0, y: 0 };
    return {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    };
  };

  // Shape mouse handlers
  const handleShapeMouseDown = (e) => {
    if (activeTool === 'pen') return;
    const pos = getMousePosition(e);
    setShapeStartPoint(pos);
    setIsDrawingShape(true);
    setCurrentShape({
      type: activeTool,
      startX: pos.x,
      startY: pos.y,
      endX: pos.x,
      endY: pos.y,
      color: strokeColor,
      width: strokeWidth
    });
  };

  const handleShapeMouseMove = (e) => {
    if (!isDrawingShape || activeTool === 'pen') return;
    const pos = getMousePosition(e);
    setCurrentShape(prev => ({
      ...prev,
      endX: pos.x,
      endY: pos.y
    }));
  };

  const handleShapeMouseUp = () => {
    if (!isDrawingShape || !currentShape) return;
    setShapes(prev => [...prev, { ...currentShape, id: Date.now() }]);
    setCurrentShape(null);
    setIsDrawingShape(false);
    setShapeStartPoint(null);
    // Save will be triggered by the useEffect watching shapes
  };
  
  // Track previous shapes length to detect new shapes added
  const prevShapesLengthRef = useRef(shapes.length);
  
  // Auto-save when shapes change (not from receiving updates)
  useEffect(() => {
    // Only trigger save if shapes were added (not cleared or received from sync)
    if (shapes.length > prevShapesLengthRef.current && !isReceivingUpdate.current) {
      console.log('[Whiteboard] Shape added, triggering save');
      saveChanges();
    }
    prevShapesLengthRef.current = shapes.length;
  }, [shapes, saveChanges]);

  // Render a shape as SVG element
  const renderShape = (shape, isPreview = false) => {
    const { type, startX, startY, endX, endY, color, width, id } = shape;
    const key = isPreview ? 'preview' : id;
    const opacity = isPreview ? 0.6 : 1;
    
    switch (type) {
      case 'line':
        return (
          <line
            key={key}
            x1={startX}
            y1={startY}
            x2={endX}
            y2={endY}
            stroke={color}
            strokeWidth={width}
            opacity={opacity}
          />
        );
      case 'rectangle':
        const rectX = Math.min(startX, endX);
        const rectY = Math.min(startY, endY);
        const rectW = Math.abs(endX - startX);
        const rectH = Math.abs(endY - startY);
        return (
          <rect
            key={key}
            x={rectX}
            y={rectY}
            width={rectW}
            height={rectH}
            stroke={color}
            strokeWidth={width}
            fill="none"
            opacity={opacity}
          />
        );
      case 'circle':
        const centerX = (startX + endX) / 2;
        const centerY = (startY + endY) / 2;
        const radiusX = Math.abs(endX - startX) / 2;
        const radiusY = Math.abs(endY - startY) / 2;
        return (
          <ellipse
            key={key}
            cx={centerX}
            cy={centerY}
            rx={radiusX}
            ry={radiusY}
            stroke={color}
            strokeWidth={width}
            fill="none"
            opacity={opacity}
          />
        );
      case 'triangle':
        const triTopX = (startX + endX) / 2;
        const triTopY = Math.min(startY, endY);
        const triBottomY = Math.max(startY, endY);
        const triLeftX = Math.min(startX, endX);
        const triRightX = Math.max(startX, endX);
        const points = `${triTopX},${triTopY} ${triLeftX},${triBottomY} ${triRightX},${triBottomY}`;
        return (
          <polygon
            key={key}
            points={points}
            stroke={color}
            strokeWidth={width}
            fill="none"
            opacity={opacity}
          />
        );
      default:
        return null;
    }
  };

  // Clear shapes only (keeps freehand drawings)
  const handleClearShapes = async () => {
    setShapes([]);
    
    // Force save with empty shapes immediately
    try {
      const paths = await canvasRef.current?.exportPaths() || [];
      const sceneData = {
        paths,
        shapes: [], // Empty shapes
        strokeColor,
        strokeWidth
      };
      
      console.log('[Whiteboard] Clearing shapes only - saving to DB');
      await fetch(`${API_BASE}/api/whiteboard/${roomId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sceneData })
      });
      
      // Broadcast to other participants
      if (whiteboard?.participants) {
        whiteboard.participants.forEach(participant => {
          if (participant.odId !== user?.sub) {
            sendPrivateMessage({
              threadId: roomId,
              type: 'whiteboard_update',
              content: JSON.stringify({ roomId, paths, shapes: [] }),
              receiverId: participant.odId,
              senderId: user?.sub,
              time: new Date().toISOString()
            });
          }
        });
      }
    } catch (err) {
      console.error('Error clearing shapes:', err);
    }
  };

  // Loading state
  if (isLoading || authLoading) {
    return (
      <LoadingContainer>
        <Spinner />
        <p>Loading whiteboard...</p>
      </LoadingContainer>
    );
  }

  // Error state
  if (error) {
    return (
      <ErrorContainer>
        <ErrorIcon>😕</ErrorIcon>
        <h2>Oops!</h2>
        <p>{error}</p>
        <Button $primary onClick={() => navigate('/workspace')} style={{ marginTop: '20px' }}>
          Go Back
        </Button>
      </ErrorContainer>
    );
  }

  // Check if whiteboard is inactive
  if (whiteboard && !whiteboard.isActive) {
    return (
      <ErrorContainer>
        <ErrorIcon>📋</ErrorIcon>
        <h2>Whiteboard Closed</h2>
        <p>This whiteboard session has ended.</p>
        <Button $primary onClick={() => navigate('/workspace')} style={{ marginTop: '20px' }}>
          Go Back
        </Button>
      </ErrorContainer>
    );
  }

  return (
    <PageContainer>
      <Header>
        <HeaderLeft>
          <Title>
            🎨 {whiteboard?.title || 'Whiteboard'}
          </Title>
          <ParticipantsBadge style={{ background: 'rgba(99, 102, 241, 0.2)', color: '#a5b4fc' }}>
            🔗 {roomId}
          </ParticipantsBadge>
          <ParticipantsBadge>
            👥 {whiteboard?.participants?.length || 1}</ParticipantsBadge>
          <StatusIndicator $saving={isSaving}>
            {isSaving ? '💾 Saving...' : '✓ Saved'}
          </StatusIndicator>
        </HeaderLeft>
        
        <HeaderRight>
          <Button onClick={syncWhiteboard} title="Sync with other participants">
            🔄 Sync
          </Button>
          <Button onClick={handleCopyLink}>
            📋 Copy Link
          </Button>
          <Button onClick={handleExport}>
            💾 Export
          </Button>
          {isAuthenticated && whiteboard?.expertId === user?.sub && (
            <Button $danger onClick={handleClose}>
              ✕ Close Session
            </Button>
          )}
        </HeaderRight>
      </Header>
      
      <Toolbar>
        <ToolbarGroup>
          <ToolbarLabel>Tools:</ToolbarLabel>
          <ToolButton $active={activeTool === 'pen'} onClick={() => selectTool('pen')} title="Pen">
            ✏️ Pen
          </ToolButton>
          <ToolButton $active={activeTool === 'line'} onClick={() => selectTool('line')} title="Line">
            📏 Line
          </ToolButton>
          <ToolButton $active={activeTool === 'rectangle'} onClick={() => selectTool('rectangle')} title="Rectangle">
            ⬜ Rect
          </ToolButton>
          <ToolButton $active={activeTool === 'circle'} onClick={() => selectTool('circle')} title="Circle">
            ⭕ Circle
          </ToolButton>
          <ToolButton $active={activeTool === 'triangle'} onClick={() => selectTool('triangle')} title="Triangle">
            🔺 Triangle
          </ToolButton>
        </ToolbarGroup>
        
        <ToolSeparator />
        
        <ToolbarGroup>
          <ToolbarLabel>Color:</ToolbarLabel>
          <ColorPicker 
            type="color" 
            value={strokeColor} 
            onChange={(e) => setStrokeColor(e.target.value)}
          />
        </ToolbarGroup>
        
        <ToolbarGroup>
          <ToolbarLabel>Size:</ToolbarLabel>
          <SizeSlider
            type="range"
            min="1"
            max="20"
            value={strokeWidth}
            onChange={(e) => setStrokeWidth(parseInt(e.target.value))}
          />
          <SizeValue>{strokeWidth}px</SizeValue>
        </ToolbarGroup>
        
        <ToolSeparator />
        
        <ToolbarGroup>
          <ToolButton $active={isEraser} onClick={toggleEraser}>
            🧹 Eraser
          </ToolButton>
          <ToolButton onClick={handleUndo}>
            ↩️ Undo
          </ToolButton>
          <ToolButton onClick={handleRedo}>
            ↪️ Redo
          </ToolButton>
          <ToolButton onClick={handleClear}>
            🗑️ Clear
          </ToolButton>
          {shapes.length > 0 && (
            <ToolButton onClick={handleClearShapes}>
              ❌ Clear Shapes
            </ToolButton>
          )}
        </ToolbarGroup>
      </Toolbar>
      
      <CanvasContainer>
        <ReactSketchCanvas
          ref={canvasRef}
          width="100%"
          height="100%"
          strokeWidth={strokeWidth}
          strokeColor={strokeColor}
          canvasColor="#ffffff"
          onChange={handleChange}
          style={{ 
            border: 'none',
            borderRadius: '0',
            pointerEvents: activeTool === 'pen' ? 'auto' : 'none',
          }}
        />
        <ShapeOverlay
          ref={shapeOverlayRef}
          $active={activeTool !== 'pen'}
          onMouseDown={handleShapeMouseDown}
          onMouseMove={handleShapeMouseMove}
          onMouseUp={handleShapeMouseUp}
          onMouseLeave={handleShapeMouseUp}
        >
          {/* Render completed shapes */}
          {shapes.map(shape => renderShape(shape))}
          {/* Render current shape being drawn */}
          {currentShape && renderShape(currentShape, true)}
        </ShapeOverlay>
      </CanvasContainer>
    </PageContainer>
  );
};

export default WhiteboardPage;
